<!--
  FundraisingFlagCard -- the SA switch that shows or hides Fundraiser /
  Donations across the product (Trello #623).

  Platform setting `enable_fundraising`, DEFAULT OFF on every environment:
  an unset row, a failed read and a malformed answer all present as OFF.
  OFF is a soft hide only: stores keep their fundraising settings and data,
  but no fundraising section, bar, line or amount is shown or charged.
  Kept as its own component so the Platform Settings page only mounts it;
  the read and the write live here.
-->
<template>
    <div class="ffc-card" data-testid="sa-setting-enable-fundraising">
        <div class="ffc-card__body">
            <div class="ffc-card__copy">
                <label class="ffc-card__label" for="sa-setting-enable-fundraising-switch">
                    Fundraising
                </label>
                <p class="ffc-card__help">
                    When on, stores can run a fundraiser: the store editor
                    shows the Fundraising section, shoppers see the fundraising
                    bar and amounts, and the amount is added to prices and
                    reports. Off by default.
                </p>
                <p class="ffc-card__help" data-testid="sa-setting-enable-fundraising-note">
                    Turning this off hides fundraising only. Stores keep their
                    fundraising settings and past orders keep what they were
                    charged, but no new order is charged a fundraising amount.
                </p>
            </div>

            <label class="ffc-toggle" data-testid="sa-setting-enable-fundraising-toggle">
                <input
                    id="sa-setting-enable-fundraising-switch"
                    v-model="enabled"
                    type="checkbox"
                    class="ffc-toggle__input"
                    data-testid="sa-setting-enable-fundraising-switch"
                    :disabled="saving"
                    @change="onToggle"
                />
                <span class="ffc-toggle__track" aria-hidden="true">
                    <span class="ffc-toggle__thumb"></span>
                </span>
                <span class="ffc-toggle__state" data-testid="sa-setting-enable-fundraising-state">{{ enabled ? "On" : "Off" }}</span>
            </label>
        </div>

        <p
            v-if="errorMsg"
            class="ffc-card__error"
            data-testid="sa-setting-enable-fundraising-error"
        >
            {{ errorMsg }}
        </p>
    </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import axios from "@/axios";

const URL = "/api/platform-settings/enable-fundraising";

// Default OFF, also when the read fails (never present the tools as on
// without an answer from the server).
const enabled = ref(false);
const saving = ref(false);
const errorMsg = ref("");

async function load() {
    errorMsg.value = "";
    try {
        const resp = await axios.get(URL);
        enabled.value =
            typeof resp?.data?.value === "boolean" ? resp.data.value : false;
    } catch (e) {
        enabled.value = false;
        errorMsg.value = "Could not load the fundraising setting.";
    }
}

async function onToggle() {
    const desired = enabled.value;
    saving.value = true;
    errorMsg.value = "";
    try {
        const resp = await axios.patch(URL, { value: desired });
        enabled.value = resp?.data?.value === true;
    } catch (e) {
        enabled.value = !desired;
        errorMsg.value =
            e?.response?.status === 403
                ? "Permission denied. Only Super Admins can change this setting."
                : "Could not save this setting.";
    } finally {
        saving.value = false;
    }
}

onMounted(load);
</script>

<style scoped>
.ffc-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.ffc-card__body {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
}
.ffc-card__copy {
    min-width: 0;
}
.ffc-card__label {
    display: block;
    font-weight: 600;
    color: #1f2937;
    font-size: 15px;
    margin-bottom: 4px;
    cursor: pointer;
}
.ffc-card__help {
    margin: 0;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    max-width: 460px;
}
.ffc-card__error {
    margin: 12px 0 0;
    color: #b91c1c;
    font-size: 13px;
}
.ffc-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex: 0 0 auto;
}
.ffc-toggle__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}
.ffc-toggle__track {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    border-radius: 999px;
    background: #cbd2dc;
    transition: background 0.15s ease;
    flex: 0 0 auto;
}
.ffc-toggle__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(28, 36, 48, 0.25);
    transition: transform 0.15s ease;
}
.ffc-toggle__input:checked + .ffc-toggle__track {
    background: #2f6df6;
}
.ffc-toggle__input:checked + .ffc-toggle__track .ffc-toggle__thumb {
    transform: translateX(18px);
}
.ffc-toggle__input:focus-visible + .ffc-toggle__track {
    outline: 2px solid #2f6df6;
    outline-offset: 2px;
}
.ffc-toggle__state {
    font-size: 13px;
    color: #5b6572;
}
</style>
